import { authApi } from "./axiosInstance";
import servicesConfig from "../config/ServicesConfig";

// Services that live on the FEED WORLD website instead of a route in this app (servicesConfig):
//   launch: "sso"      - My Market: the seller dashboard of the user's shop (market ticket);
//   launch: "website"  - a website page (websitePath), e.g. Feed World or EPM, signed in as a member.
// Both open in a new tab, so this app stays open in its own.
export const isSsoService = (serviceKey) => ["sso", "website"].includes(servicesConfig[serviceKey]?.launch);

// A new tab, opened straight away while the click is still being handled (browsers block tabs
// opened later, e.g. after the ticket request). It shows "Opening FEED WORLD…" until the website loads.
// Null when the browser blocked it anyway - the service then opens in this tab.
const openBlankTab = () => {
  const tab = window.open("", "_blank");
  if (!tab) return null;
  try {
    tab.document.title = "Opening the website…";
    tab.document.body.innerHTML = '<p style="font:16px system-ui,sans-serif;color:#475569;padding:32px">Opening the website…</p>';
  } catch (e) {
    // a blank tab of our own is always writable; ignore if a browser says otherwise
  }
  tab.name = `feedworld-${Date.now()}`;
  return tab;
};

// Gets a one-time ticket from the feed service and form-POSTs it to FEED WORLD, which signs the user
// in. A form POST (not a URL) keeps the ticket out of logs and browser history. With `tab`, the
// website opens there; if the ticket can't be had, the tab shows `fallbackUrl` (signed out) or closes.
// Protocol: unifiedplatform docs/SSO_JAVA.md (My Markets, website members) and docs/FPO_PAGES.md.
const submitTicket = ({ ticket, callbackUrl }, next, tab = null) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = callbackUrl;
  if (tab) form.target = tab.name;
  for (const [name, value] of Object.entries({ ticket, ...(next ? { next } : {}) })) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
  form.remove();
};

const openWithTicket = async (ticketPath, next, failure, { tab = null, fallbackUrl = null } = {}) => {
  try {
    const { data } = await authApi.post(ticketPath);
    submitTicket(data.data, next, tab);
    return true;
  } catch (error) {
    if (tab && fallbackUrl) {
      tab.location.href = fallbackUrl;
      return false;
    }
    if (tab) tab.close();
    alert(error.response?.data?.error?.message || failure);
    return false;
  }
};

// My Markets: lands on the seller dashboard of the user's shop, in a new tab.
export const openMyMarkets = (next) =>
  openWithTicket("/sso/market-ticket", next, "My Markets could not be opened. Please try again.", { tab: openBlankTab() });

// A website page of a service tile (servicesConfig websitePath), in a new tab, signed in there as a
// member. Without a ticket it still opens, signed out (the page asks to log in where it needs to).
export const openWebsitePage = (path) =>
  openWithTicket("/sso/member-ticket", path, "The website could not be opened. Please try again.",
    { tab: openBlankTab(), fallbackUrl: `${FEED_WORLD_SITE_URL}${path}` });

// Opens a website-launched service (see isSsoService). Call it straight from the click handler.
export const openSsoService = (serviceKey) => {
  const config = servicesConfig[serviceKey];
  return config?.launch === "website" ? openWebsitePage(config.websitePath || "/") : openMyMarkets();
};

// The FEED Startup Government Dashboard on FEED WORLD (/government-dashboard), in this tab: Government officers
// with an office area and System Administrators (the "government" ticket; others get 403). Rejects with the
// server's message when the ticket can't be had, so the page can offer to try again.
export const openGovernmentDashboard = async () => {
  try {
    const { data } = await authApi.post("/sso/government-ticket");
    submitTicket(data.data, null);
  } catch (error) {
    throw new Error(error.response?.data?.error?.message || "Your dashboard could not be opened. Please try again.");
  }
};

// The FPO's web page editor on FEED WORLD (FPO admins only).
export const openFpoPageEditor = () =>
  openWithTicket("/sso/fpo-page-ticket", null, "The web page editor could not be opened. Please try again.");

// The same for a My Business company's web page (the business admin who owns it only).
export const openBusinessPageEditor = () =>
  openWithTicket("/sso/business-page-ticket", null, "The web page editor could not be opened. Please try again.");

// The FEED WORLD website (Header's "FEED WORLD website" button). REACT_APP_FEEDWORLD_URL in .env, e.g.
// http://127.0.0.1:8000 locally; without it, the same host on port 80 (the server: UI :8080, website :80).
export const FEED_WORLD_SITE_URL =
  (process.env.REACT_APP_FEEDWORLD_URL || `http://${window.location.hostname}`).replace(/\/+$/, "");

// ---- FEED WORLD website members (unifiedplatform docs/SSO_JAVA.md, "Website members") ----------
// The website's Login / Register send visitors to "/?return=feedworld" or "/register?return=feedworld".
// The mark is kept for this tab (it survives Register -> Login) for 30 minutes; after signing in the
// visitor goes back to the website, signed in there too, instead of to /home.
const FEEDWORLD_RETURN_KEY = "feedworldReturn";
const FEEDWORLD_RETURN_MINUTES = 30;

export const rememberFeedWorldReturn = (search) => {
  if (new URLSearchParams(search).get("return") === "feedworld") {
    sessionStorage.setItem(FEEDWORLD_RETURN_KEY, String(Date.now()));
  }
};

// Read-once: whether this sign-in should go back to the website.
export const takeFeedWorldReturn = () => {
  const markedAt = Number(sessionStorage.getItem(FEEDWORLD_RETURN_KEY));
  sessionStorage.removeItem(FEEDWORLD_RETURN_KEY);
  return markedAt > 0 && Date.now() - markedAt < FEEDWORLD_RETURN_MINUTES * 60 * 1000;
};

// Signs the current user in to the website and opens it. Resolves false when that failed.
export const returnToFeedWorld = () =>
  openWithTicket("/sso/member-ticket", null, "The website could not be opened. Please try again.");

// The Header's "FEED WORLD website" button: opens the website's home already signed in there too, so
// members-only pages (e.g. the Feed World magazine) open straight away. If that sign-in can't be
// made, the website still opens, signed out.
export const openFeedWorldWebsite = async () => {
  try {
    const { data } = await authApi.post("/sso/member-ticket");
    const { ticket, callbackUrl } = data.data;
    const form = document.createElement("form");
    form.method = "POST";
    form.action = callbackUrl;
    for (const [name, value] of Object.entries({ ticket, next: "/" })) {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }
    document.body.appendChild(form);
    form.submit();
  } catch (error) {
    window.location.href = FEED_WORLD_SITE_URL;
  }
};

// {exists, canEdit, url, qrUrl, name, tagline, publishedAt} of the user's FPO web page.
export const getFpoPageStatus = async () => (await authApi.get("/sso/fpo-page")).data.data;

// The same for the user's My Business company web page (/mybusiness/about).
export const getBusinessPageStatus = async () => (await authApi.get("/sso/business-page")).data.data;
